import { useEffect, useRef, useState } from 'react';

/**
 * 특정 섹션으로 스크롤을 이동하는 함수를 제공하고, 현재 보여지는 섹션을 감지합니다.
 * @param targetElements
 * @param rootMargin
 * @returns selectedSection, moveScroll, setSelectedSection
 *
 */
const useScrollIntoView = (
	targetElements: keyof HTMLElementTagNameMap,
	rootMargin = '0px 0px -300px 0px',
) => {
	type TargetElements = HTMLElementTagNameMap[typeof targetElements];
	const sectionsRef = useRef<TargetElements[]>([]);

	useEffect(() => {
		const sections = document?.querySelectorAll(targetElements);
		sectionsRef.current = [...sections];
	}, [targetElements]);

	/** 해당하는 섹션의 타이틀과 매치되는 위치로 스크롤을 이동합니다. */
	const moveScroll = (sectionTitle: string) => {
		if (sectionsRef.current.length === 0) return;
		const target = sectionsRef.current.find(
			(section) => section.innerText === sectionTitle,
		);
		target?.scrollIntoView({ behavior: 'smooth' });
	};

	const [selectedSection, setSelectedSection] = useState<string | null>(null);

	useEffect(() => {
		if (sectionsRef.current.length === 0) return;
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						const section = sectionsRef.current.find(
							(section) => section === entry.target,
						);
						setSelectedSection(section?.innerText ?? null);
					}
				});
			},
			{ threshold: 1, rootMargin: rootMargin },
		);
		sectionsRef.current.forEach((section) => observer.observe(section));
		return () => observer.disconnect();
	}, [sectionsRef, rootMargin]);

	return { selectedSection, moveScroll, setSelectedSection };
};

export default useScrollIntoView;
